'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useEffect, useState, useRef } from 'react'

// lib
import { ENV } from '@lib/env'
import { addStyles, distribute } from '../../_support'
import NativeTextInput from './native-text-input'
import Box from '../../box'
import {
  InputContainer,
  InputContainerProps,
  ThemedInputContainer
} from '../input'

/* -----------------------------------------------------------------------------
 * TextInput
 *
 * This is a small wrapper around the base RN TextInput. It is used to normalize
 * and fix behavior between platforms.
 * -------------------------------------------------------------------------- */

const ThemedTextInput = NativeTextInput

type ThemedTextInputProps = React.ComponentProps<typeof ThemedTextInput>

export type TextInputProps = InputContainerProps &
  ThemedTextInputProps & {
    children?: React.ReactNode
    invisible?: boolean
    labeless?: boolean
    compact?: boolean
  }

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      children,
      labeless,
      invisible,
      compact,
      style = {},
      ...props
    }: TextInputProps,
    forwadedRef: React.MutableRefObject<HTMLInputElement>
  ) => {
    const defaultRef = useRef<HTMLInputElement>(null)
    const ref = forwadedRef || defaultRef

    // RNW TextInput does not provide multiline autogrow out of the box
    const [height, setHeight] = useState(0)
    useEffect(() => {
      if (ENV['client_type'] === 'web' && props.multiline) {
        const node = ref.current
        const cachedHeight = node.style.height

        node.style.height = 'inherit'
        setHeight(node.scrollHeight)
        node.style.height = cachedHeight
      }
    }, [props.value])

    // TODO: remove when RN adds proper support for TextInput lineHeight
    const styleArr = addStyles(style, {
      position: 'relative',
      lineHeight: undefined
    } as any)
    if (ENV['client_type'] === 'web') {
      styleArr.push({ outlineWidth: 0, outlineStyle: 'none' })
      props.multiline && styleArr.push({ height })
    }

    if (invisible) {
      styleArr.push({ width: 0, paddingLeft: 0, paddingRight: 0 })
    }

    if (labeless) {
      if (compact) {
        styleArr.push({
          paddingTop: '10px',
          paddingBottom: '10px',
          minHeight: 40
        })
      } else {
        styleArr.push({
          paddingTop: '20px',
          paddingBottom: '20px'
        })
      }
    }

    const containerStyleProps: InputContainerProps = {
      style: { overflow: 'hidden' }
    }

    const inputStyleProps: ThemedTextInputProps = {
      space: '00',
      underlineColorAndroid: 'transparent',
      style: styleArr
    }

    const [containerProps, inputProps] = distribute(props, {
      using: ThemedInputContainer.styleKeys,
      to: { ...containerStyleProps },
      rest: { ...inputStyleProps }
    })

    return (
      <InputContainer flex='0 0 auto' {...containerProps}>
        <Box flex='1 1 auto'>
          {children}
          <ThemedTextInput ref={ref} flex='1 1 auto' {...inputProps} />
        </Box>
      </InputContainer>
    )
  }
)

export default TextInput
